/**
 * UpgradeAccountForm — promote an anonymous (guest_{hex}) account into
 * a named, recoverable one. Phase 4 / anon-accounts (2026-04-13).
 *
 * The player is already logged in via their anon session. This form
 * POSTs new credentials to /api/session_auth/upgrade/, which edits the
 * existing User row in place — no data is lost, no re-login required.
 *
 * Password is required. Username, email, first/last name are all
 * optional — leaving a field blank keeps whatever the anon account
 * already had (generated guest username, placeholder email).
 */

import React, {Component} from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import FormGroup from 'react-bootstrap/FormGroup';
import Row from 'react-bootstrap/Row';
import {Link} from 'react-router-dom';
import {LOGO_URL} from '../../Settings';


export default class UpgradeAccountForm extends Component {

    constructor(props) {
        super(props);
        const currentUser = (props.authenticator && props.authenticator.user)
            || {};
        this.state = {
            errors: {},
            submitted: false,
            isSaved: true,
            username: currentUser.username || '',
            email: '',
            apassword: '',
            apassword2: '',
            firstname: currentUser.first_name || '',
            lastname: currentUser.last_name || '',
            passwordInvalid: false,
        };

        this.onFieldChange = this.onFieldChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
        this.onSubmitFailure = this.onSubmitFailure.bind(this);
        this.passwordCheck = this.passwordCheck.bind(this);
    }

    // Discord-linked accounts sign in through Discord and carry a
    // system-issued placeholder email they cannot edit — the backend
    // /upgrade/ endpoint rejects any email change from them. Hide the
    // email field (and strip it from the payload) for those viewers.
    isDiscordLinked() {
        const currentUser = (this.props.authenticator
            && this.props.authenticator.user) || {};
        return !!currentUser.is_discord_linked;
    }

    onFieldChange(event) {
        const fieldName = event.target.name;
        const newState = {isSaved: false};
        newState[fieldName] = event.target.value;
        this.setState(newState, () => this.passwordCheck());
    }

    passwordCheck() {
        if (this.state.apassword && this.state.apassword2
                && this.state.apassword !== this.state.apassword2) {
            this.setState({
                passwordInvalid: true,
                errors: {...this.state.errors, apassword2: 'Does not match.'},
            });
        } else {
            const newErrors = {...this.state.errors};
            delete newErrors.apassword2;
            this.setState({
                passwordInvalid: false,
                errors: newErrors,
            });
        }
    }

    onSubmitSuccess(data) {
        if (!data) {
            return;
        }
        this.setState({submitted: true});
    }

    onSubmitFailure(data) {
        this.setState({isSaved: false, errors: data || {}});
    }

    onSubmit(event) {
        event.preventDefault();
        if (this.state.passwordInvalid) {
            return;
        }
        this.setState({isSaved: true});
        const payload = {
            password: this.state.apassword,
        };
        if (this.state.username) {
            payload.username = this.state.username;
        }
        if (this.state.email && !this.isDiscordLinked()) {
            payload.email = this.state.email;
        }
        if (this.state.firstname) {
            payload.first_name = this.state.firstname;
        }
        if (this.state.lastname) {
            payload.last_name = this.state.lastname;
        }
        this.props.authenticator.upgradeAccount(
            payload,
            this.onSubmitSuccess,
            this.onSubmitFailure,
        );
    }

    render() {
        if (this.state.submitted) {
            return <Container style={{color: 'white'}}>
                <Row style={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '1rem'}}>
                    <Col>
                        <img
                            src={LOGO_URL}
                            alt={'hsh-logo'}
                            style={{display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '50%'}}
                        />
                    </Col>
                    <Col>
                        <h1>Account Upgraded</h1>
                        <h3>Your credentials are saved. Use them to sign in from another device.</h3>
                    </Col>
                </Row>
                <Link to="/"><Button variant={'seed'}>Back to the Hotel</Button></Link>
            </Container>;
        }

        return <Container style={{color: 'white'}}>
            <Row style={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '1rem'}}>
                <Col>
                    <img
                        src={LOGO_URL}
                        alt={'hsh-logo'}
                        style={{display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '50%'}}
                    />
                </Col>
                <Col>
                    <h1>Upgrade Your Guest Account</h1>
                </Col>
            </Row>
            <Row style={{textAlign: 'center'}}>
                <p>You're playing as a guest. Set a password (and optionally an email and custom username) so you can recover your account from another device.</p>
                <p>Your game history and stats stay with you &mdash; this just adds a way to sign back in.</p>
            </Row>
            <Form onSubmit={this.onSubmit} validated={!this.state.isSaved}>
                {this.state.errors && this.state.errors.detail &&
                    <Alert variant={'danger'}>{this.state.errors.detail}</Alert>}
                <FormGroup>
                    <Form.Label column={'username'}>
                        Username (optional &mdash; keep guest name or choose one)
                    </Form.Label>
                    <Form.Control type="text"
                                  id={'username'}
                                  name={'username'}
                                  autoComplete="username"
                                  onChange={this.onFieldChange}
                                  value={this.state.username}
                                  isInvalid={!!this.state.errors.username}
                    />
                    <Form.Control.Feedback type="invalid">
                        {this.state.errors.username}
                    </Form.Control.Feedback>

                    <Form.Label column={'apassword'}>
                        Password (required)
                    </Form.Label>
                    <Form.Control type="password"
                                  id={'apassword'}
                                  name={'apassword'}
                                  autoComplete="new-password"
                                  onChange={this.onFieldChange}
                                  value={this.state.apassword}
                                  isInvalid={!!this.state.errors.password}
                                  placeholder={'Input New Password'}
                                  minLength={9}
                                  required
                    />
                    <Form.Control.Feedback type="invalid">
                        {this.state.errors.password}
                    </Form.Control.Feedback>

                    <Form.Label column={'apassword2'}>
                        Confirm Password
                    </Form.Label>
                    <Form.Control type="password"
                                  id={'apassword2'}
                                  name={'apassword2'}
                                  autoComplete="new-password"
                                  onChange={this.onFieldChange}
                                  value={this.state.apassword2}
                                  isInvalid={!!this.state.errors.apassword2}
                                  minLength={9}
                                  required
                    />
                    <Form.Control.Feedback type="invalid">
                        {this.state.errors.apassword2}
                    </Form.Control.Feedback>

                    {this.isDiscordLinked()
                        ? <p>This account signs in through Discord. Your email is managed by Discord and can&rsquo;t be edited here.</p>
                        : <>
                            <Form.Label column={'email'}>
                                Email (optional &mdash; enables password resets)
                            </Form.Label>
                            <Form.Control type="email"
                                          id={'email'}
                                          name={'email'}
                                          onChange={this.onFieldChange}
                                          value={this.state.email}
                                          isInvalid={!!this.state.errors.email}
                            />
                            <Form.Control.Feedback type="invalid">
                                {this.state.errors.email}
                            </Form.Control.Feedback>
                        </>}

                    <Form.Label column={'firstname'}>First Name (optional)</Form.Label>
                    <Form.Control type="text"
                                  id={'firstname'}
                                  name={'firstname'}
                                  onChange={this.onFieldChange}
                                  value={this.state.firstname}
                                  isInvalid={!!this.state.errors.first_name}
                    />

                    <Form.Label column={'lastname'}>Last Name (optional)</Form.Label>
                    <Form.Control type="text"
                                  id={'lastname'}
                                  name={'lastname'}
                                  onChange={this.onFieldChange}
                                  value={this.state.lastname}
                                  isInvalid={!!this.state.errors.last_name}
                    />
                </FormGroup>
                <Button type={'submit'}
                        variant={'stem'}
                        style={{width: '100%'}}
                        disabled={this.state.isSaved || this.state.passwordInvalid}>
                    Upgrade Account
                </Button>
            </Form>
            <Link to="/"><Button variant={'seed'}>Back to the Hotel</Button></Link>
        </Container>;
    }
}
